import React, { useState, useCallback, useEffect } from 'react';
import {
  Modal,
  Button,
  Icon,
  Text,
  BlockStack,
  Spinner,
} from '@shopify/polaris';
import {
  ChevronLeftMinor,
  ChevronRightMinor,
  ContentMinor,
} from '@shopify/polaris-icons';
import { generationScenesService } from '../../services/generationScenesService';
import AuthenticatedAssetImage from '../AuthenticatedAssetImage';

const GenerationStoryPreviewModal = ({ 
  isOpen, 
  onClose, 
  selectedStory 
}) => {
  const [currentGenerationStoryImageIndex, setCurrentGenerationStoryImageIndex] = useState(0);
  const [generationStoryScenes, setGenerationStoryScenes] = useState([]);
  const [isLoadingGenerationStoryScenes, setIsLoadingGenerationStoryScenes] = useState(false);

  const handleClose = () => {
    onClose();
    setCurrentGenerationStoryImageIndex(0); // Reset index when closing
  };

  const fetchGenerationStoryScenes = useCallback(async (slug) => {
    if (!slug) return;
    
    setIsLoadingGenerationStoryScenes(true);
    try {
      const data = await generationScenesService.getScenesByStorySlug(slug);
      setGenerationStoryScenes(Array.isArray(data) ? data : []);
      setCurrentGenerationStoryImageIndex(0);
    } catch (err) {
      setGenerationStoryScenes([]);
    } finally {
      setIsLoadingGenerationStoryScenes(false);
    }
  }, []);

  // Fetch scenes when modal opens
  useEffect(() => {
    if (isOpen && selectedStory) {
      fetchGenerationStoryScenes(selectedStory.slug);
    }
  }, [isOpen, selectedStory, fetchGenerationStoryScenes]);

  if (!selectedStory) return null;

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      title={<div style={{padding: '16px'}}>{`${selectedStory.title} - Story Scenes`}</div>}
      primaryAction={{
        content: 'Close',
        onAction: handleClose,
      }}
      footer={
        generationStoryScenes.length > 1 ? (
          <div style={{padding: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px'}}>
            <Button
              icon={ChevronLeftMinor}
              size="slim"
              onClick={() => setCurrentGenerationStoryImageIndex(prev => 
                prev === 0 ? generationStoryScenes.length - 1 : prev - 1
              )}
            />
            <Text variant="bodyMd" tone="subdued">
              {currentGenerationStoryImageIndex + 1} of {generationStoryScenes.length}
            </Text>
            <Button
              icon={ChevronRightMinor}
              size="slim"
              onClick={() => setCurrentGenerationStoryImageIndex(prev => 
                prev === generationStoryScenes.length - 1 ? 0 : prev + 1
              )}
            />
          </div>
        ) : (
          <div style={{padding: '16px'}}></div>
        )
      }
    >
      {isLoadingGenerationStoryScenes ? (
        <BlockStack gap="400" align="center">
          <Spinner size="large" />
          <Text variant="bodyMd" tone="subdued">Loading story scenes...</Text>
        </BlockStack>
      ) : generationStoryScenes.length > 0 ? (
        <BlockStack gap="400">
          {/* Image Display */}
          <div style={{ 
            position: 'relative', 
            width: '100%', 
            height: '400px', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            backgroundColor: '#f6f6f7',
            borderRadius: '8px',
            overflow: 'hidden'
          }}>
            <AuthenticatedAssetImage
              src={generationStoryScenes[currentGenerationStoryImageIndex].scene_url} 
              assetSize="preview"
              alt={`Scene ${generationStoryScenes[currentGenerationStoryImageIndex].order || currentGenerationStoryImageIndex + 1}`}
              style={{ 
                maxWidth: '100%', 
                maxHeight: '100%', 
                objectFit: 'contain' 
              }} 
            />
          </div>
          
          {/* Scene Info */}
          <div style={{ textAlign: 'center' }}>
            <Text variant="bodyMd">
              Scene {generationStoryScenes[currentGenerationStoryImageIndex].order || currentGenerationStoryImageIndex + 1}
            </Text>
            <Text variant="bodyMd" tone="subdued">
              {generationStoryScenes[currentGenerationStoryImageIndex].type || 'Scene'}
            </Text>
            {generationStoryScenes[currentGenerationStoryImageIndex].text && (
              <Text variant="bodyMd" tone="subdued" style={{ marginTop: '8px' }}>
                {generationStoryScenes[currentGenerationStoryImageIndex].text}
              </Text>
            )}
          </div>
        </BlockStack>
      ) : (
        <BlockStack gap="400" align="center">
          <Icon source={ContentMinor} color="subdued" />
          <Text variant="bodyMd" tone="subdued">No scenes found for this story</Text>
        </BlockStack>
      )}
    </Modal>
  );
};

export default GenerationStoryPreviewModal; 